import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FilingStatusTracker = ({ filings, onViewReceipt, onDownloadReturn }) => {
  const getStatusIcon = (status) => {
    switch (status) {
      case 'submitted': return 'Send';
      case 'acknowledged': return 'CheckCircle';
      case 'processing': return 'Clock';
      case 'accepted': return 'CheckCircle2';
      case 'rejected': return 'XCircle';
      default: return 'Circle';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'submitted': return 'text-primary bg-primary/10 border-primary/20';
      case 'acknowledged': return 'text-success bg-success/10 border-success/20';
      case 'processing': return 'text-warning bg-warning/10 border-warning/20';
      case 'accepted': return 'text-success bg-success/10 border-success/20';
      case 'rejected': return 'text-error bg-error/10 border-error/20';
      default: return 'text-muted-foreground bg-muted/10 border-border';
    }
  };

  const getProgressPercentage = (status) => {
    switch (status) {
      case 'submitted': return 25;
      case 'acknowledged': return 50;
      case 'processing': return 75;
      case 'accepted': return 100;
      case 'rejected': return 100;
      default: return 0;
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">Filing Status Tracker</h3>
        <Button variant="ghost" size="sm" iconName="RefreshCw" iconPosition="left">
          Refresh Status
        </Button>
      </div>
      <div className="space-y-6">
        {filings?.map((filing) => (
          <div key={filing?.id} className="border border-border rounded-lg p-4">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h4 className="font-medium text-foreground">{filing?.period}</h4>
                <p className="text-sm text-muted-foreground">
                  Filed on {new Date(filing.filedDate)?.toLocaleDateString()}
                </p>
                <p className="text-sm text-muted-foreground">
                  Reference: {filing?.reference}
                </p>
              </div>
              <div className="text-right">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(filing?.status)}`}>
                  <Icon name={getStatusIcon(filing?.status)} size={14} className="mr-1" />
                  {filing?.status?.charAt(0)?.toUpperCase() + filing?.status?.slice(1)}
                </span>
                <p className="text-sm text-muted-foreground mt-1">
                  Amount: ${filing?.amount?.toLocaleString()}
                </p>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mb-4">
              <div className="flex justify-between text-xs text-muted-foreground mb-1">
                <span>Progress</span>
                <span>{getProgressPercentage(filing?.status)}%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all duration-300 ${
                    filing?.status === 'rejected' ? 'bg-error' : 'bg-primary'
                  }`}
                  style={{ width: `${getProgressPercentage(filing?.status)}%` }}
                />
              </div>
            </div>

            {/* Timeline */}
            <div className="space-y-3 mb-4">
              {filing?.timeline?.map((event, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className={`w-2 h-2 rounded-full mt-2 ${
                    event?.completed ? 'bg-success' : 'bg-muted'
                  }`} />
                  <div className="flex-1">
                    <p className={`text-sm ${
                      event?.completed ? 'text-foreground' : 'text-muted-foreground'
                    }`}>
                      {event?.description}
                    </p>
                    {event?.timestamp && (
                      <p className="text-xs text-muted-foreground">
                        {new Date(event.timestamp)?.toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="flex items-center space-x-2 pt-4 border-t border-border">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onViewReceipt(filing)}
                iconName="FileText"
                iconPosition="left"
              >
                View Receipt
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onDownloadReturn(filing)}
                iconName="Download"
                iconPosition="left"
              >
                Download Return
              </Button>
              {filing?.paymentLink && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => window.open(filing?.paymentLink, '_blank')}
                  iconName="ExternalLink"
                  iconPosition="left"
                >
                  Pay Online
                </Button>
              )}
            </div>

            {/* Additional Info */}
            {filing?.notes && (
              <div className="mt-4 p-3 bg-muted/20 rounded-lg">
                <p className="text-sm text-muted-foreground">{filing?.notes}</p>
              </div>
            )}
          </div>
        ))}
      </div>
      {filings?.length === 0 && (
        <div className="text-center py-8">
          <Icon name="FileText" size={48} className="mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No filings to track yet.</p>
        </div>
      )}
    </div>
  );
};

export default FilingStatusTracker;